/**
 * MultiImageUploader — manage an ordered LIST of image URLs for a
 * single entity (currently: Product).
 *
 * iter121 — extends the existing single-image ``ImageUploader`` UX
 * to N images. First image in the array is the "primary" (used by
 * product cards / cart thumbnails); every image is rendered in the
 * customer PDP carousel with dot indicators + swipe.
 *
 * Reuses the same ``POST /api/uploads`` endpoint the platform-wide
 * ``ImageUploader`` uses — no new backend contract. Callers pass the
 * current ``value`` (string[]) and receive ``onChange(next: string[])``.
 *
 * Backward compat: safe to pass ``value=[]`` or ``value=[single_url]``
 * — the wire format stays the same as the pre-existing
 * ``images: List[str]`` field on Product.
 */
import { useRef, useState } from "react";
import { Upload, Loader2, Plus, X, Star, ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { requestCameraPermission, hasPromptedForCamera, isNativeApp } from "../lib/permissions";
import { useLse } from "../lib/lse";
import { compressImage } from "../lib/imageCompress";

const MAX_IMAGES = 8;

export default function MultiImageUploader({
    value,
    onChange,
    testId = "multi-image",
    className = "",
}) {
    // Location Service Engine (image_compression): large photos are shrunk on the device before upload.
    const compress = useLse().has("image_compression");
    const images = Array.isArray(value) ? value.filter(Boolean) : [];
    const inp = useRef(null);
    const [busy, setBusy] = useState(false);

    const openPicker = async (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (busy || images.length >= MAX_IMAGES) return;
        if (isNativeApp() && !hasPromptedForCamera()) {
            try { await requestCameraPermission(); } catch { /* fail-open */ }
        }
        inp.current?.click();
    };

    /** Upload each picked file sequentially and append to the list.
     *  Sequential (not parallel) so a slow file doesn't block the UI
     *  and toasts fire in the right order. Duplicates are skipped so
     *  the merchant can't accidentally add the same image twice. */
    const handleFiles = async (files) => {
        const picks = Array.from(files || []);
        if (picks.length === 0) return;
        const room = MAX_IMAGES - images.length;
        if (picks.length > room) {
            toast.info(`Max ${MAX_IMAGES} images — only the first ${room} were kept.`);
        }
        const next = [...images];
        setBusy(true);
        try {
            for (const picked of picks.slice(0, room)) {
                const f = compress ? await compressImage(picked) : picked;
                if (!/^image\//.test(f.type)) {
                    toast.error(`Skipped ${f.name} — not an image`);
                    continue;
                }
                if (f.size > 8 * 1024 * 1024) {
                    toast.error(`Skipped ${f.name} — over 8 MB`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                try {
                    const { data } = await api.post("/uploads", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    if (data?.url && !next.includes(data.url)) {
                        next.push(data.url);
                        onChange([...next]);          // stream updates so the grid re-renders per upload
                    }
                } catch (e) {
                    toast.error(e?.response?.data?.detail || `Failed: ${f.name}`);
                }
            }
            toast.success("Uploaded");
        } finally {
            setBusy(false);
        }
    };

    const removeAt = (idx) => {
        const next = images.filter((_, i) => i !== idx);
        onChange(next);
    };
    const moveTo = (from, to) => {
        if (to < 0 || to >= images.length) return;
        const next = [...images];
        const [row] = next.splice(from, 1);
        next.splice(to, 0, row);
        onChange(next);
    };
    const makePrimary = (idx) => moveTo(idx, 0);

    return (
        <div
            className={className}
            data-testid={testId}
            // iter122 — this uploader is often rendered inside the
            // admin/merchant ``<Field>`` component which wraps its
            // children in a ``<label>``. A click anywhere inside a
            // label fires the label's default action = activate the
            // first descendant form control (our hidden file input).
            // That caused the picker to open twice on the "Add" tile
            // AND, more disruptively, opened the picker whenever the
            // user tapped a per-tile Remove / Arrow / Set-primary
            // button — completely breaking the intended action.
            // preventDefault on the root wrapper suppresses the
            // label's activation for every click inside; each button
            // still runs its own onClick normally.
            onClick={(e) => e.preventDefault()}
        >
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {images.map((url, i) => (
                    <div
                        key={url}
                        data-testid={`${testId}-tile-${i}`}
                        className={`relative aspect-square rounded-xl overflow-hidden border-2 ${i === 0 ? "border-primary" : "border-border"}`}
                    >
                        <img src={url} alt={`img-${i}`} className="w-full h-full object-cover" />
                        {i === 0 && (
                            <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                                <Star size={9} className="fill-current" /> Primary
                            </span>
                        )}
                        <button
                            type="button"
                            data-testid={`${testId}-remove-${i}`}
                            onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                            className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-full p-1"
                            title="Remove"
                        >
                            <X size={12} />
                        </button>
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent flex items-center justify-between px-1 py-0.5">
                            <button
                                type="button"
                                data-testid={`${testId}-left-${i}`}
                                onClick={(e) => { e.stopPropagation(); moveTo(i, i - 1); }}
                                disabled={i === 0}
                                className="text-white p-0.5 disabled:opacity-30"
                                title="Move left"
                            ><ArrowLeft size={12} /></button>
                            {i !== 0 && (
                                <button
                                    type="button"
                                    data-testid={`${testId}-primary-${i}`}
                                    onClick={(e) => { e.stopPropagation(); makePrimary(i); }}
                                    className="text-[9px] font-bold uppercase text-white"
                                    title="Make primary"
                                >Set primary</button>
                            )}
                            <button
                                type="button"
                                data-testid={`${testId}-right-${i}`}
                                onClick={(e) => { e.stopPropagation(); moveTo(i, i + 1); }}
                                disabled={i === images.length - 1}
                                className="text-white p-0.5 disabled:opacity-30"
                                title="Move right"
                            ><ArrowRight size={12} /></button>
                        </div>
                    </div>
                ))}
                {images.length < MAX_IMAGES && (
                    <button
                        type="button"
                        data-testid={`${testId}-add`}
                        onClick={openPicker}
                        disabled={busy}
                        className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/60 bg-muted/30 flex flex-col items-center justify-center text-muted-foreground text-xs font-semibold gap-1 disabled:opacity-60"
                    >
                        {busy ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                Uploading…
                            </>
                        ) : (
                            <>
                                {images.length === 0 ? <Upload size={20} /> : <Plus size={20} />}
                                {images.length === 0 ? "Add images" : "Add more"}
                            </>
                        )}
                    </button>
                )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
                {images.length}/{MAX_IMAGES} images · first image is used as the product thumbnail. Drag arrows to reorder.
            </p>
            <input
                ref={inp}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/jpg"
                multiple
                data-testid={`${testId}-file-input`}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                    e.stopPropagation();
                    // iter122 bug fix — ``e.target.files`` is a LIVE
                    // FileList that empties the moment we reset
                    // ``e.target.value = ""``. We must snapshot the
                    // files into a plain Array first, otherwise
                    // handleFiles receives 0 items. This exact race
                    // was why the admin/merchant "Add images" tile
                    // appeared to do nothing — the upload never fired
                    // because the file list was already empty by the
                    // time we iterated it.
                    const picked = Array.from(e.target.files || []);
                    e.target.value = "";
                    handleFiles(picked);
                }}
                className="hidden"
            />
        </div>
    );
}
